import type { AirHealth } from '@/types/airTraffic'
import type { AirFilters } from './AirMap'
import { AIR, CLASS_COLOR, CLASS_LABEL, FONT_MONO, type AircraftClass } from './theme'
import { SectionLabel } from './ui'

const FEED_LABEL: Record<string, string> = {
  opensky: 'OpenSky (worldwide)',
  mil: 'adsb.lol military',
  sqk7700: 'adsb.lol 7700',
  sqk7500: 'adsb.lol 7500',
  sqk7600: 'adsb.lol 7600',
  ladd: 'adsb.lol LADD',
  pia: 'adsb.lol PIA',
  cells: 'adsb.lol area cells',
}

const ALT_BANDS: Array<{ id: AirFilters['altBand']; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'high', label: '>FL250' },
  { id: 'mid', label: '10k–25k' },
  { id: 'low', label: '<10k ft' },
  { id: 'ground', label: 'Ground' },
]

const LEGEND: AircraftClass[] = ['emergency', 'watched', 'military', 'privacy', 'civil']

interface Props {
  filters: AirFilters
  onFilters: (f: AirFilters) => void
  basemap: 'dark' | 'satellite'
  onBasemap: (b: 'dark' | 'satellite') => void
  showAirports: boolean
  onShowAirports: (v: boolean) => void
  health: AirHealth | null
  zoom: number
}

export default function LayerRail(p: Props) {
  const f = p.filters
  const set = (patch: Partial<AirFilters>) => p.onFilters({ ...f, ...patch })

  return (
    <aside
      className="flex w-[216px] shrink-0 flex-col overflow-y-auto border-r"
      style={{ background: AIR.panel, borderColor: AIR.line }}
    >
      <SectionLabel>Filter aircraft</SectionLabel>
      <div className="space-y-2 px-3">
        <input
          id="air-filter-text"
          value={f.text}
          onChange={(e) => set({ text: e.target.value })}
          placeholder="Type, operator, callsign…"
          className="h-7 w-full rounded-sm px-2 text-[12px] outline-none placeholder:opacity-60"
          style={{ background: AIR.bg, color: AIR.text, boxShadow: `inset 0 0 0 1px ${AIR.lineStrong}` }}
        />
        <div className="flex flex-wrap gap-1">
          {ALT_BANDS.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => set({ altBand: b.id })}
              aria-pressed={f.altBand === b.id}
              className="rounded-sm px-1.5 py-0.5 text-[10px]"
              style={{
                fontFamily: FONT_MONO,
                color: f.altBand === b.id ? AIR.bg : AIR.muted,
                background: f.altBand === b.id ? AIR.accent : 'transparent',
                boxShadow: f.altBand === b.id ? undefined : `inset 0 0 0 1px ${AIR.line}`,
              }}
            >
              {b.label}
            </button>
          ))}
        </div>
        <Toggle id="air-mil-only" label="Military only" checked={f.milOnly} onChange={(v) => set({ milOnly: v })} />
      </div>

      <SectionLabel>Map</SectionLabel>
      <div className="space-y-2 px-3">
        <div className="grid grid-cols-2 gap-1">
          {(['dark', 'satellite'] as const).map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => p.onBasemap(b)}
              aria-pressed={p.basemap === b}
              className="rounded-sm py-1 text-[11px] capitalize"
              style={{
                color: p.basemap === b ? AIR.bright : AIR.muted,
                background: p.basemap === b ? AIR.panel2 : 'transparent',
                boxShadow: `inset 0 0 0 1px ${p.basemap === b ? AIR.lineStrong : AIR.line}`,
              }}
            >
              {b}
            </button>
          ))}
        </div>
        <Toggle
          id="air-airports"
          label="Airports & ATC"
          hint={p.zoom < 5 ? 'zoom in' : undefined}
          checked={p.showAirports}
          onChange={p.onShowAirports}
        />
      </div>

      <SectionLabel>Legend</SectionLabel>
      <div className="space-y-1 px-3 text-[11px]">
        {LEGEND.map((c) => (
          <div key={c} className="flex items-center gap-2" style={{ color: AIR.text }}>
            <svg width="12" height="12" viewBox="0 0 64 64" aria-hidden>
              <path
                d="M32 3 35.5 10 35.5 24 59 37 59 42 35.5 35 35.5 50 44 56 44 60 32 57 20 60 20 56 28.5 50 28.5 35 5 42 5 37 28.5 24 28.5 10Z"
                fill={CLASS_COLOR[c]}
              />
            </svg>
            {CLASS_LABEL[c]}
          </div>
        ))}
        <div className="flex items-center gap-2" style={{ color: AIR.text }}>
          <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ boxShadow: `0 0 0 1.5px ${AIR.accent}` }} />
          Airport with ATC audio
        </div>
      </div>

      <div className="mt-auto">
        <SectionLabel>Data feeds</SectionLabel>
        <div className="space-y-1 px-3 pb-3 text-[11px]">
          {!p.health && <div style={{ color: AIR.muted }}>Connecting…</div>}
          {p.health &&
            Object.entries(p.health.feeds)
              .sort(([a], [b]) => (FEED_LABEL[a] || a).localeCompare(FEED_LABEL[b] || b))
              .map(([name, h]) => {
                const color = h.ok ? AIR.accent : h.ok === false ? AIR.emergency : AIR.military
                return (
                  <div key={name} className="flex items-center justify-between gap-2" title={h.error || undefined}>
                    <span className="flex min-w-0 items-center gap-1.5" style={{ color: AIR.text }}>
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: color }} />
                      <span className="truncate">{FEED_LABEL[name] || name}</span>
                    </span>
                    <span className="shrink-0" style={{ color: AIR.muted, fontFamily: FONT_MONO }}>
                      {h.age != null ? `${h.age}s` : h.error ? 'wait' : '—'}
                    </span>
                  </div>
                )
              })}
          {p.health?.mode === 'economy' && (
            <p className="pt-1.5 leading-snug" style={{ color: AIR.military }}>
              adsb.lol has no API key here, so it allows 500 requests a day: military and emergency layers refresh every
              2–5 min and worldwide traffic comes from OpenSky every 90 s.
              {p.health.adsb_daily_remaining != null && (
                <span style={{ fontFamily: FONT_MONO }}> {p.health.adsb_daily_remaining} left today.</span>
              )}
            </p>
          )}
          {p.health?.opensky_credits != null && (
            <div className="flex justify-between pt-1" style={{ color: AIR.muted }}>
              <span>OpenSky credits</span>
              <span style={{ fontFamily: FONT_MONO }}>{p.health.opensky_credits}</span>
            </div>
          )}
        </div>
      </div>
    </aside>
  )
}

function Toggle({ id, label, checked, onChange, hint }: { id: string; label: string; checked: boolean; onChange: (v: boolean) => void; hint?: string }) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-center justify-between text-[12px]" style={{ color: AIR.text }}>
      <span>
        {label}
        {hint && (
          <span className="ml-1.5 text-[10px]" style={{ color: AIR.muted }}>
            {hint}
          </span>
        )}
      </span>
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-3.5 w-3.5 accent-teal-500" />
    </label>
  )
}
